import {theme as antdTheme} from 'antd';
import type {ThemeConfig} from 'antd';
import {CAP_DARK, CAP_NEUTRAL, CAP_REVAMP} from './capTokens';

export type ThemeMode = 'light' | 'dark';

/**
 * Bridges the app's existing per-tenant branding JSON (public/theme/{tenantID}.json,
 * consumed today by CSfetchGlobalData.ts to build the MUI theme) into an antd v6
 * ThemeConfig, so components migrated to antd stay visually consistent with the
 * tenant's brand without a second, hand-maintained source of truth.
 *
 * `branding` is the same shape CSfetchGlobalData already works with:
 *  - palette: the flat MUI-style palette used for `muiTheme.palette = palette`
 *  - CSTheme: the nested `configurations.theme` object (typography/shape/etc.)
 *
 * COLOR PRECEDENCE — explicit product choice: the "AST Console Revamp" design
 * tokens (capTokens.ts CAP_REVAMP) are forced for every antd color token,
 * overriding whatever a tenant's theme JSON defines for primary/secondary/
 * error/warning/success/text/background/border. This makes every client look
 * like Capillary's own redesigned console rather than that tenant's brand
 * color (e.g. Connect's blue #269bdd is no longer shown). Other non-color
 * tokens (heading sizes) are unaffected and still come from the tenant's
 * CSTheme as before; borderRadius stays tenant-overridable (defaulting to
 * CAP_REVAMP's 8px when a tenant doesn't set one). fontFamily is forced like
 * the colors above — every tenant's custom brand typeface (e.g. Connect's
 * "regular"/"bold"/... @font-face names) is replaced by CAP_REVAMP's Roboto.
 */

interface PaletteColor {
  main?: string;
  contrastText?: string;
}

interface BrandingPalette {
  primary?: PaletteColor;
  secondary?: PaletteColor;
  error?: PaletteColor;
  warning?: PaletteColor;
  success?: PaletteColor;
  text?: {primary?: string; secondary?: string};
  background?: {default?: string; paper?: string};
}

interface BrandingCSTheme {
  palette?: {divider?: string};
  shape?: {borderRadius?: number};
  typography?: {
    fontFamily?: string;
    h1?: {fontSize?: string};
    h2?: {fontSize?: string};
    h3?: {fontSize?: string};
    h4?: {fontSize?: string};
    h5?: {fontSize?: string};
  };
}

interface BrandingSource {
  palette?: BrandingPalette;
  CSTheme?: BrandingCSTheme;
}

// tenant typography sizes are expressed in rem (e.g. "2.0rem"); antd's
// Typography component tokens want raw px numbers.
const remToPx = (value?: string): number | undefined => {
  if (!value) return undefined;
  const rem = parseFloat(value);
  return Number.isNaN(rem) ? undefined : rem * 16;
};

const buildAntdTheme = (branding: BrandingSource = {}, mode: ThemeMode = 'light'): ThemeConfig => {
  const {CSTheme = {}} = branding;
  const tenantBorderRadius = CSTheme.shape?.borderRadius;
  const isDark = mode === 'dark';

  // Loosely typed so we can also carry a few custom, non-standard-antd
  // keys (colorSecondary/colorOnSecondary/colorAccent/colorSecondaryBg/
  // colorAccentBg) through theme.useToken() for components (e.g. Footer,
  // or any badge/pill matching the mockup's semantic-subtle-bg pattern)
  // that need a palette color with no standard antd token equivalent.
  //
  // CAP_REVAMP colors are forced (not just fallbacks) — see the file-level
  // comment above. `palette`/`borderColor` are intentionally unused for color
  // tokens now; kept as params since non-color tokens below still read CSTheme.
  //
  // LIGHT/DARK — unlike the earlier BiFrost-only pass, CAP_REVAMP ships a
  // complete authored dark palette (from the same mockup), so dark mode no
  // longer just leans on antd's darkAlgorithm for neutrals/text/border — it
  // gets real, designed values too. Background still seeds from CAP_DARK
  // (bgBase/surface), which the mockup's own dark bg-canvas/bg-surface agree
  // with exactly.
  const revamp = isDark ? CAP_REVAMP.dark : CAP_REVAMP;
  const revampPrimary = isDark ? CAP_REVAMP.dark.primary : CAP_REVAMP.primary;
  const revampSecondary = isDark ? CAP_REVAMP.dark.secondary : CAP_REVAMP.secondary;
  const revampAccent = isDark ? CAP_REVAMP.dark.accent : CAP_REVAMP.accent;
  const revampSemantic = isDark ? CAP_REVAMP.dark.semantic : CAP_REVAMP.semantic;

  const token: Record<string, unknown> = {
    colorPrimary: revampPrimary.base,
    colorPrimaryHover: revampPrimary.hover,
    colorPrimaryActive: revampPrimary.press,
    colorPrimaryBg: revampPrimary.subtle,
    colorSecondary: revampSecondary.base,
    colorSecondaryBg: revampSecondary.subtle,
    colorOnSecondary: CAP_NEUTRAL.black,
    // Orange accent/highlight color — no standard antd slot fits this (it's
    // not tied to "warning" semantics), so it's a custom token like colorSecondary.
    colorAccent: revampAccent.base,
    colorAccentBg: revampAccent.subtle,
    colorError: revampSemantic.error.fg,
    // Pin the text variant too so antd doesn't derive a different shade for
    // form-item/validation messages — every error message uses the same red.
    colorErrorText: revampSemantic.error.fg,
    colorErrorBg: revampSemantic.error.bg,
    colorWarning: revampSemantic.warning.fg,
    colorWarningBg: revampSemantic.warning.bg,
    colorSuccess: revampSemantic.success.fg,
    colorSuccessBg: revampSemantic.success.bg,
    colorInfo: revampSemantic.info.fg,
    colorInfoBg: revampSemantic.info.bg,
    // Custom token for the "neutral" badge kind (mockup's .badge-neutral) —
    // no standard antd semantic slot for a plain grey pill.
    colorNeutralFg: revampSemantic.neutral.fg,
    colorNeutralBg: revampSemantic.neutral.bg,
    colorTextBase: revamp.text.primary,
    colorTextSecondary: revamp.text.secondary,
    colorTextTertiary: revamp.text.tertiary,
    colorBorder: revamp.border.default,
    colorBorderSecondary: revamp.border.subtle,
    ...(!isDark && {
      colorBgLayout: CAP_REVAMP.bg.canvas,
      colorBgContainer: CAP_REVAMP.bg.surface,
      colorFillAlter: CAP_REVAMP.bg.surface2,
      boxShadowSecondary: CAP_REVAMP.shadow.lg,
    }),
    ...(isDark && {
      // Seeds antd's darkAlgorithm (see colorBgBase in capTokens.ts) so every
      // derived dark surface — colorBgLayout, colorBgElevated, etc. — comes
      // out as a shade of this navy instead of antd's default near-black.
      // Matches the mockup's own --bg-canvas/--bg-surface for dark exactly.
      colorBgBase: CAP_DARK.bgBase,
      colorBgContainer: CAP_DARK.surface,
      colorBgElevated: CAP_DARK.surface,
      colorFillAlter: CAP_REVAMP.dark.bg.surface2,
      // Floating panels (DatePicker, Select dropdown, Dropdown menu, Popover,
      // Modal, ...) use boxShadowSecondary for their only depth cue. Neither
      // antd's default near-invisible white glow nor the mockup's own shadow
      // recipe (tuned for a light backdrop — rgba(23,27,33,x) barely reads
      // against an already-dark surface) provides real separation here, so a
      // crisp 1px border ring (reusing the same colorBorder every input/card
      // border already uses, via its CSS var so it always matches) plus a
      // real dark shadow is kept instead, regardless of how close the two
      // navy tones are perceptually. The var() fallback points at
      // CAP_DARK.surface rather than a hand-computed hex, so it can't drift
      // out of sync if CAP_DARK.bgBase ever changes. The rgba(0,0,0,X) layers
      // are plain shadow-depth/opacity tuning, not brand colors, so they're
      // left as literals like any standard shadow recipe.
      boxShadowSecondary: `
        0 0 0 1px var(--ant-color-border, ${CAP_DARK.surface}),
        0 6px 16px 0 rgba(0, 0, 0, 0.48),
        0 3px 6px -4px rgba(0, 0, 0, 0.56),
        0 9px 28px 8px rgba(0, 0, 0, 0.36)
      `,
    }),
    boxShadow: CAP_REVAMP.shadow.sm,
    // Falls back to CAP_REVAMP's 8px when the tenant doesn't specify its own
    // shape — see the file-level comment. Not gated by isDark: it's a shape
    // choice, not a color one, so it applies the same in both modes.
    borderRadius:
      typeof tenantBorderRadius === 'number' ? tenantBorderRadius : CAP_REVAMP.radius.md,
    // Explicit product choice: every container (Card, Modal, table wrappers,
    // panels using token.borderRadiusLG) uses a flat 4px radius.
    borderRadiusLG: CAP_REVAMP.radius.sm,
    borderRadiusSM: CAP_REVAMP.radius.sm,
    fontFamily: CAP_REVAMP.fontFamily,
  };

  // The mockup's Input/Select focus ring is blue-toned (colorSecondary),
  // distinct from colorPrimary green used for buttons/active-tab/brand
  // elsewhere — so it's a per-component override rather than a global
  // controlOutline/activeBorderColor change. hoverBorderColor is pinned back
  // to the resting border color because the mockup defines no hover state
  // for unfocused fields (only :focus is styled) — antd's own default would
  // otherwise show a green hover tint before focus.
  //
  // borderRadius/borderRadiusLG: explicit product choice — every input field
  // (Input, Select, DatePicker, ...) stays at a flat 4px (CAP_REVAMP.radius.sm)
  // regardless of size, rather than the global 8px/12px radius scale Button/
  // Card use. Scoped to just these form-control components.
  const focusFieldTokens = {
    hoverBorderColor: revamp.border.default,
    activeBorderColor: revamp.border.focus,
    controlOutlineWidth: 3,
    borderRadius: CAP_REVAMP.radius.sm,
    borderRadiusLG: CAP_REVAMP.radius.sm,
    borderRadiusSM: CAP_REVAMP.radius.sm,
  };

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token,
    components: {
      Input: {
        ...focusFieldTokens,
        activeShadow: `0 0 0 3px ${revampSecondary.subtle}`,
      },
      Select: {
        ...focusFieldTokens,
        activeOutlineColor: revampSecondary.subtle,
      },
      DatePicker: {
        ...focusFieldTokens,
        activeShadow: `0 0 0 3px ${revampSecondary.subtle}`,
      },
      // Explicit product choice (overrides the mockup's own radius-md/8px
      // button spec): every button stays at a flat 4px (CAP_REVAMP.radius.sm)
      // regardless of size, matching Input/Select/DatePicker above. Scoped to
      // just Button — Card/Modal/Dropdown etc. use the global 4px borderRadiusLG.
      Button: {
        borderRadius: CAP_REVAMP.radius.sm,
        borderRadiusLG: CAP_REVAMP.radius.sm,
        borderRadiusSM: CAP_REVAMP.radius.sm,
      },
      Typography: {
        ...(remToPx(CSTheme.typography?.h1?.fontSize) && {
          fontSizeHeading1: remToPx(CSTheme.typography?.h1?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h2?.fontSize) && {
          fontSizeHeading2: remToPx(CSTheme.typography?.h2?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h3?.fontSize) && {
          fontSizeHeading3: remToPx(CSTheme.typography?.h3?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h4?.fontSize) && {
          fontSizeHeading4: remToPx(CSTheme.typography?.h4?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h5?.fontSize) && {
          fontSizeHeading5: remToPx(CSTheme.typography?.h5?.fontSize),
        }),
      },
    },
  };
};

export default buildAntdTheme;
